import { useState } from "react";
import { formatMoney, quantity as toQuantity } from "@tovi/shared";
import { Button, Modal, useI18n, useTranslate } from "@tovi/ui";
import type { CartLine } from "@/lib/cart.ts";
import { displayName } from "@/lib/menu.ts";
import {
  asksSomething,
  chosenIn,
  defaultsFor,
  pick,
  unanswered,
  type ChoiceGroup,
  type Choices,
  type Chosen,
} from "@/lib/modifiers.ts";
import type { MenuItem } from "@/screens/service.tsx";

/**
 * The item sheet (`08-pos-terminal-and-qr.md` § A, step 2).
 *
 * "Item detail sheet handles specifications, toppings, tags, quantity, and
 * per-item notes. Selection constraints are explicit: *'Selectable X–Y, Select
 * Z'* and *'Multiple Select'* — meal sets and topping groups carry min/max
 * rules that the UI must enforce and explain."
 *
 * Enforce **and explain** is the part worth getting right: a sheet that
 * silently refuses to close is a cashier holding up a queue while they work
 * out which group they missed. So the constraint is on screen before it is
 * broken, and the button says what is still needed.
 *
 * The rules themselves are in `lib/modifiers.ts`. What is here is the asking.
 *
 * **Built from the spec text.** With no capture, the sheet is a centred modal
 * over the grid; a real till may want a right-side drawer against the cart.
 */
export function ItemSheet({
  item,
  choices,
  loading,
  currency,
  onClose,
  onAdd,
}: {
  item: MenuItem;
  choices: Choices;
  /** The catalogues are still coming. What this item asks is not known yet. */
  loading: boolean;
  currency: string;
  onClose: () => void;
  onAdd: (line: CartLine) => void;
}) {
  const t = useTranslate();
  const { locale } = useI18n();
  const [count, setCount] = useState(1);
  const [note, setNote] = useState("");
  const [picked, setPicked] = useState<Chosen | null>(null);
  /**
   * Nothing picked yet means the merchant's defaults, computed each render
   * rather than seeded into state: the groups can arrive after the sheet has
   * opened, and an initial value would have been taken before they did. The
   * first tap freezes it, so a default can never come back over a choice.
   */
  const chosen = picked ?? defaultsFor(choices);

  const unitPrice = BigInt(item.sellPrice);
  // The same name the tile showed, and the same one the cart line will carry.
  const name = displayName(item, locale);

  const specifications = chosenIn(choices.specifications, chosen);
  const toppings = chosenIn(choices.toppings, chosen);
  const options = [...specifications, ...toppings];
  const unit = options.reduce((sum, option) => sum + option.price, unitPrice);
  // A sheet that adds at base price because its groups had not arrived is the
  // silent version of the bug this whole sheet exists to prevent, so an item
  // that asks something waits for the question rather than skipping it.
  const waiting = loading && asksSomething(item);
  const missing = unanswered(choices, chosen);

  return (
    <Modal
      open
      title={name}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("pos.item.cancel")}
          </Button>
          <Button
            disabled={waiting || missing.length > 0}
            onClick={() =>
              onAdd({
                key: crypto.randomUUID(),
                id: item.id,
                name,
                unitPrice: { amount: unitPrice, currency: currency as "USD" },
                quantity: toQuantity(BigInt(count)),
                isFired: false,
                // The prices for the engine and the words for everything else.
                // A free option still belongs in `modifiers`: what it costs is
                // nothing, but a line that was specified must not be merged
                // into by the next tap on the tile.
                ...(options.length > 0
                  ? {
                      modifiers: options.map((option) => ({
                        amount: option.price,
                        currency: currency as "USD",
                      })),
                    }
                  : {}),
                ...(specifications.length > 0 ? { specifications } : {}),
                ...(toppings.length > 0 ? { toppings } : {}),
                // Trimmed away to nothing is no note: it would print a blank
                // line on the ticket and stop the next tap merging into this.
                ...(note.trim() ? { note: note.trim() } : {}),
              })
            }
          >
            {missing.length > 0
              ? t("pos.item.choose", { group: missing[0]!.name })
              : t("pos.item.add", {
                  total: formatMoney((unit * BigInt(count)).toString(), currency),
                })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* The code sits with the price rather than in the title, which takes a
            string and not a node — and which is where the name already is. */}
        <div className="flex items-baseline gap-3">
          <p className="text-lg font-semibold text-ink-body">
            {formatMoney(unitPrice.toString(), currency)}
          </p>
          {item.number ? <p className="text-sm text-ink-muted">{item.number}</p> : null}
        </div>

        {waiting ? <p className="text-sm text-ink-muted">{t("pos.menu.loading")}</p> : null}

        {[...choices.specifications, ...choices.toppings].map((group) => (
          <Group
            key={group.id}
            group={group}
            chosen={chosen[group.id] ?? EMPTY}
            currency={currency}
            onPick={(optionId) => setPicked(pick(chosen, group, optionId))}
          />
        ))}

        <div className="flex items-center gap-3">
          <span className="text-sm text-ink-muted">{t("pos.item.quantity")}</span>
          <Button variant="secondary" onClick={() => setCount(Math.max(1, count - 1))}>
            −
          </Button>
          <span className="w-10 text-center text-lg font-semibold text-ink-body">{count}</span>
          <Button variant="secondary" onClick={() => setCount(count + 1)}>
            +
          </Button>
        </div>

        <label className="flex flex-col gap-1 text-sm text-ink-muted">
          {t("pos.item.note")}
          <input
            className="h-11 rounded-control border border-line bg-surface px-3 text-sm text-ink-body"
            // What `OrderLineInput.note` accepts. The field stops rather than
            // letting a queued fire be rejected for a length nobody was shown.
            maxLength={240}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t("pos.item.notePlaceholder")}
          />
        </label>
      </div>
    </Modal>
  );
}

/**
 * One group of answers.
 *
 * A `fieldset` and its `legend` rather than a heading and a div: the options
 * are one control with several buttons in it, and that is the markup that says
 * so to a screen reader. `aria-pressed` says which way each one is.
 *
 * What the group wants is beside its name, always — not only once it has been
 * broken. A rule a cashier reads before they choose costs nothing; one they
 * meet as a disabled button costs the queue.
 */
function Group({
  group,
  chosen,
  currency,
  onPick,
}: {
  group: ChoiceGroup;
  chosen: readonly string[];
  currency: string;
  onPick: (optionId: string) => void;
}) {
  const t = useTranslate();

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-2 text-sm font-medium text-ink-body">
        {group.name}{" "}
        <span className="font-normal text-ink-muted">
          ·{" "}
          {group.isRequired
            ? group.isSingle
              ? t("pos.item.chooseOne")
              : t("pos.item.chooseAny")
            : t("pos.item.optional")}
        </span>
      </legend>
      <div className="flex flex-wrap gap-2">
        {group.options.map((option) => {
          const isChosen = chosen.includes(option.id);
          return (
            <Button
              key={option.id}
              variant={isChosen ? "primary" : "secondary"}
              aria-pressed={isChosen}
              onClick={() => onPick(option.id)}
            >
              {option.name}
              {option.price === 0n ? null : (
                <span className="text-xs">
                  {option.price > 0n ? "+" : ""}
                  {formatMoney(option.price.toString(), currency)}
                </span>
              )}
            </Button>
          );
        })}
      </div>
    </fieldset>
  );
}

const EMPTY: readonly string[] = [];
